/**
 * Constants.ts
 *
 * Module to hold logging-related constants
 */

export interface ICustomProperties {
	CampaignId?: string;
	CorrelationId?: string;
	Count?: number;
	Data?: string;
	ErrorMessage?: string;
	FeedbackType?: number;
	PanelType?: number;
	FileName?: string;
	Flights?: string;
	HttpStatusCode?: number;
	HttpStatusText?: string;
	IsBugEnabled?: boolean;
	IsDiagnosticsIncluded?: boolean;
	IsEmailIncluded?: boolean;
	IsEmailPolicyEnabled?: boolean;
	IsIdeaEnabled?: boolean;
	IsScreenshotIncluded?: boolean;
	Message?: string;
	SessionId?: string;
	SurveyId?: string;
	SurveyType?: number;
	TimeMilliseconds?: number;
	Type?: string;
	ClientFeedbackId?: string;
	PromptAutoDismiss?: string;
	AdditionalSurveyInfo?: string;
	SurveyActivityInfo?: string;
	PersonalizerUserAction?: number;
	PersonalizerEventId?: string;
	GovernanceRequestBody?: string;
	GovernanceResponseText?: string;
	ActualSetAutoDismissValue?: number;
	CVFormId?: string;
}

export interface IEventId {
	name: string;
}

export class EventIds {
	public static InApp = class {
		public static UI = class {
			public static Picker = class {
				public static Shown = class {
					public static VALUE: IEventId = { name: "InApp_UI_Picker_Shown" };
				};
			};

			public static Form = class {
				public static Shown = class {
					public static VALUE: IEventId = { name: "InApp_UI_Form_Shown" };
				};

				public static Submit = class {
					public static VALUE: IEventId = { name: "InApp_UI_Form_Submit" };
				};
			};
		};
	};

	// This saves ~400 bytes (minified) vs nested classes
	public static SURVEY_FLOODGATE_CAMPAIGNLOAD_FAILED: IEventId = { name: "Survey_Floodgate_CampaignLoad_Failed" };
	public static SURVEY_FLOODGATE_FILEREAD_FAILED: IEventId = { name: "Survey_Floodgate_FileRead_Failed" };
	public static SURVEY_FLOODGATE_FILEWRITE_FAILED: IEventId = { name: "Survey_Floodgate_FileWrite_Failed" };
	public static SURVEY_FLOODGATE_TRIGGERMET: IEventId = { name: "Survey_Floodgate_TriggerMet" };
	public static SURVEY_FLOODGATE_USERSELECTED: IEventId = { name: "Survey_Floodgate_UserSelected" };
	public static SURVEY_UI_FORM_SHOWN: IEventId = { name: "Survey_UI_Form_Shown" };
	public static SURVEY_UI_FORM_DISPATCHER_IMPLEMENTED: IEventId = { name: "Survey_UI_Form_Dispatcher_Implemented" };
	public static SURVEY_UI_FORM_SUBMIT: IEventId = { name: "Survey_UI_Form_Submit" };
	public static SURVEY_UI_FORM_SECONDARY_SUBMIT: IEventId = { name: "Survey_UI_Form_Secondary_Submit" };
	public static SURVEY_UI_FORM_SUBMIT_ERROR: IEventId = { name: "Survey_UI_Form_Submit_Error" };
	public static SURVEY_UI_FORM_USERCLOSED: IEventId = { name: "Survey_UI_Form_UserClosed" };
	public static SURVEY_UI_THANKYOUPAGE_USERCLOSED: IEventId = { name: "Survey_UI_ThankYouPage_UserClosed" };
	public static SURVEY_UI_FORM_NO_RETURN_TYPE: IEventId = { name: "Survey_UI_Form_No_Return_Type"};

	public static SURVEY_UI_PROMPT_AUTODISMISSED: IEventId = { name: "Survey_UI_Prompt_AutoDismissed" };
	public static SURVEY_UI_THANKYOUPAGE_AUTODISMISSED: IEventId = { name: "Survey_UI_ThankYouPage_AutoDismissed" };
	public static SURVEY_UI_PROMPT_SHOWN: IEventId = { name: "Survey_UI_Prompt_Shown" };
	public static SURVEY_UI_PROMPT_USERCLOSED: IEventId = { name: "Survey_UI_Prompt_UserClosed" };
	public static SURVEY_UI_ADDITIONALPROMPT_USERCLOSED: IEventId = { name: "Survey_UI_AdditionalPrompt_UserClosed" };
	public static SURVEY_UI_PROMPT_CLICKED: IEventId = { name: "Survey_UI_Prompt_Clicked" };
	public static SURVEY_UI_ADDITIONALPROMPT_CLICKED: IEventId = { name: "Survey_UI_AdditionalPrompt_Clicked" };
	public static SURVEY_UI_REDIRECTIONFAILURE: IEventId = { name: "Survey_UI_RedirectionFailure" };
	public static GOVERNANCE_SERVICEFAILURE: IEventId = { name: "Gorvenance_ServiceFailure" };
	public static GOVERNANCE_SERVICERESULT: IEventId = { name: "Gorvenance_ServiceResult" };
	public static DYNAMICTRANSPORT_POST_ERROR: IEventId = { name: "DynamicTransport_Post_Error" };
	public static FEEDBACK_SURVEYSDISABLED_AADC: IEventId = {name: "Feedback_SurveysDisabled_AADC"};
	public static SURVEY_UI_DISABLED_AADC: IEventId = { name: "Survey_UI_Prevented_AADC" };
	public static PERSONALIZER_SERVICEREWARDRESULT: IEventId = { name: "Personalizer_ServiceRewardResult" };
	public static PERSONALIZER_CLIENTFAILURE: IEventId = { name: "Personalizer_ClientFailure" };
	public static APPDATA_JSON_PARSE_ERROR: IEventId = { name: "AppData_JSON_Parse_Error" };
	public static SURVEY_RENDER_OVERRIDE_ERROR: IEventId = { name: "Survey_Render_Override_Error" };
	public static SURVEY_RENDER_OVERRIDE_SUCCESS: IEventId = { name: "Survey_Render_Override_Success" };
	public static SURVEY_EVENT_KEY_ERROR: IEventId = { name: "Survey_Event_Key_Error" };

	public static Shared = class {
		public static Upload = class {
			public static Failed = class {
				public static VALUE: IEventId = { name: "Shared_Upload_Failed" };
			};
			public static Prevented = class {
				public static VALUE: IEventId = { name: "Shared_Upload_Prevented_AADC" };
			};
		};

		public static Screenshot = class {
			public static Render = class {
				public static Failed = class {
					public static VALUE: IEventId = { name: "Shared_Screenshot_Render_Failed" };
				};
				public static Success = class {
					public static VALUE: IEventId = { name: "Shared_Screenshot_Render_Success" };
				};
			};

			public static GetContent = class {
				public static Failed = class {
					public static DefaultImage = class {
						public static Returned = class {
							public static VALUE: IEventId = { name: "Shared_Screenshot_GetContent_Failed_DefaultImage_Returned" };
						};
					};
				};
			};
		};
	};

	public static WebSurfaces = class {
		public static Common = class {
			public static Error = class {
				public static VALUE: IEventId = { name: "WebSurfaces_Common_Error" };
			};
			public static Info = class {
				public static VALUE: IEventId = { name: "WebSurfaces_Common_Info" };
			};
		};
		public static CampaignMetadataAggregator = class {
			public static Error = class {
				public static VALUE: IEventId = { name: "WebSurfaces_CampaignMetadataAggregator_Error" };
			};
			public static Requests = class {
				public static VALUE: IEventId = { name: "WebSurfaces_CampaignMetadataAggregator_Requests" };
			};
		};
		public static SetCampaignAction = class {
			public static Error = class {
				public static VALUE: IEventId = { name: "WebSurfaces_SetCampaignAction_Error" };
			};
			public static Requests = class {
				public static VALUE: IEventId = { name: "WebSurfaces_SetCampaignAction_Requests" };
			};
		};
	};

	public static UserFacts = class {
		public static Common = class {
			public static Error = class {
				public static VALUE: IEventId = { name: "UserFacts_Common_Error" };
			};
			public static Info = class {
				public static VALUE: IEventId = { name: "UserFacts_Common_Info" };
			};
		};

		public static LocalFact = class {
			public static Error = class {
				public static VALUE: IEventId = { name: "UserFacts_LocalFact_Error" };
			};
		};

		public static Messaging = class {
			public static Requests = class {
				public static VALUE: IEventId = { name: "UserFacts_Messaging_Requests" };
			};
		};

		public static AugLoop = class {
			public static Requests = class {
				public static VALUE: IEventId = { name: "UserFacts_AugLoop_Requests" };
			};
			public static Error = class {
				public static VALUE: IEventId = { name: "UserFacts_AugLoop_Error" };
			};
		};

		public static Provider = class {
			public static Info = class {
				public static VALUE: IEventId = { name: "UserFacts_Provider_Summary" };
			};
		};
	};

	public static Common = class {
		public static Error = class {
			public static VALUE: IEventId = { name: "Common_Error" };
		};
		public static Info = class {
			public static VALUE: IEventId = { name: "Common_Info" };
		};
	};
}
